/**
 * Client-Error-Telemetrie (Reporter).
 *
 * Faengt unbehandelte Browser-Fehler ab und schickt sie gebatcht an den
 * Collector (automations /api/v1/client-logs -> public.infra_client_logs).
 * Damit landen Fehler aus JEDER App serverseitig und sind per psql abfragbar,
 * ohne dass Logs manuell weitergereicht werden muessen.
 *
 * Design-Invarianten:
 *  - Idempotent + browser-only + no-op auf localhost.
 *  - NIE den Client stoeren: alles in try/catch, kein throw, kein eigenes
 *    console.error (sonst Endlosschleife durch den console.error-Wrap).
 *  - Self-exclude: eigene Collector-Requests werden nie geloggt.
 *  - Kein React-State -> der console.error-Wrap loest NIE einen Re-Render aus
 *    (wichtig, sonst wuerde er Hydration-Fehler wie React #418 verschlimmern).
 *  - Harte Caps gegen Flooding (pro Session + Dedupe).
 */

interface LogEntry {
  level: string;
  message: string;
  stack?: string;
  app?: string;
  url?: string;
  app_version?: string;
}

const MAX_SESSION = 50;
const MAX_QUEUE = 20;
const FLUSH_MS = 5000;

let installed = false;
let collectorUrl: string | null = null;
let appName = "";
let appVersion: string | undefined;

let sessionCount = 0;
const queue: LogEntry[] = [];
const seen = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Normalisiert einen Pfad fuer die Coverage (dynamische Segmente -> :id), damit
// die Kardinalitaet begrenzt bleibt (z.B. /chat/<uuid> -> /chat/:id). Datensparsam:
// nur das Pfad-Muster, KEINE Query/Token/IDs.
function normalizeRoute(path: string): string {
  const out = path
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ":id"; // UUID
      if (/^[0-9]+$/.test(seg)) return ":id"; // numerische ID
      if (/^[0-9a-f]{16,}$/i.test(seg)) return ":id"; // langer Hex-Token
      return seg;
    })
    .join("/");
  return out || "/";
}

// Einmaliger anonymer Coverage-Beacon pro Seitenaufruf (App + Route + Version).
// Macht "0 Fehler" interpretierbar: gerendert + 0 Fehler = sauber.
function sendCoverage() {
  if (!collectorUrl) return;
  try {
    const url = collectorUrl.replace("/client-logs", "/client-coverage");
    const body = JSON.stringify({
      app: appName,
      route: normalizeRoute(window.location.pathname),
      app_version: appVersion,
    });
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
    } else {
      void fetch(url, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        credentials: "omit",
        mode: "cors",
      }).catch(() => {
        /* swallow */
      });
    }
  } catch {
    /* ignore */
  }
}

function resolveCollectorUrl(): string | null {
  const override = process.env.NEXT_PUBLIC_CLIENTLOG_URL;
  if (override) return override;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) return null;
  const labels = host.split(".");
  if (labels.length < 2) return null;
  const root = labels.slice(-2).join("."); // janeway.io | janewaylabs.com
  return `https://automations.${root}/api/v1/client-logs`;
}

function detectAppVersion(): string | undefined {
  try {
    const meta = document.querySelector('meta[name="app-version"]')?.getAttribute("content");
    if (meta) return meta;
    const w = (window as unknown as { __JANEWAY_APP_VERSION__?: string }).__JANEWAY_APP_VERSION__;
    if (typeof w === "string" && w) return w;
  } catch {
    /* ignore */
  }
  return undefined;
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush(false), FLUSH_MS);
}

function flush(useBeacon: boolean) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0 || !collectorUrl) return;
  const entries = queue.splice(0, queue.length);
  const payload = JSON.stringify({ entries });
  try {
    if (useBeacon && typeof navigator.sendBeacon === "function") {
      // text/plain -> "simple request", kein CORS-Preflight (Beacon kann den
      // Preflight-Fehler nicht behandeln). Der Collector parst rohen Text.
      navigator.sendBeacon(collectorUrl, new Blob([payload], { type: "text/plain" }));
    } else {
      void fetch(collectorUrl, {
        method: "POST",
        body: payload,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        credentials: "omit",
        mode: "cors",
      }).catch(() => {
        /* swallow — kein console.error, sonst Schleife */
      });
    }
  } catch {
    /* ignore */
  }
}

function enqueue(level: string, message: string, stack?: string) {
  if (!collectorUrl || sessionCount >= MAX_SESSION) return;
  const msg = (message || "").trim();
  if (!msg) return;
  // Self-exclude: eigene Collector-Requests / -Fehler nie loggen (Loop-Schutz).
  if (msg.includes("/api/v1/client-logs")) return;

  const sig = `${level}|${msg.slice(0, 200)}|${stack?.split("\n")[1]?.trim() ?? ""}`;
  if (seen.has(sig)) return;
  seen.add(sig);

  sessionCount++;
  queue.push({
    level,
    message: msg,
    stack,
    app: appName,
    // Nie href: Fragment/Query tragen auf /auth/callback und /onboarding
    // access_token/refresh_token/invite (ST-R2-gui-elements-1).
    url: window.location.origin + window.location.pathname,
    app_version: appVersion,
  });

  if (queue.length >= MAX_QUEUE) flush(false);
  else scheduleFlush();
}

function formatConsoleArgs(args: unknown[]): { message: string; stack?: string } {
  let stack: string | undefined;
  const parts = args.map((a) => {
    if (a instanceof Error) {
      stack = stack ?? a.stack;
      return a.message;
    }
    if (typeof a === "string") return a;
    try {
      return JSON.stringify(a);
    } catch {
      return String(a);
    }
  });
  return { message: parts.join(" "), stack };
}

/**
 * Installiert den Client-Logger einmalig. Browser-only, no-op auf localhost.
 * Wird global im ThemeProvider gemountet -> laeuft in JEDER App.
 */
export function installClientLogger(): void {
  if (installed || typeof window === "undefined") return;
  collectorUrl = resolveCollectorUrl();
  if (!collectorUrl) return; // localhost / unbekannter Host -> deaktiviert
  installed = true;

  appName = window.location.hostname.split(".")[0] || "";
  appVersion = detectAppVersion();

  // Coverage: dieser Seitenaufruf wurde gerendert (einmal pro Full-Page-Load).
  sendCoverage();

  window.addEventListener("error", (e: ErrorEvent) => {
    const message = e.message || e.error?.message || "Unhandled error";
    enqueue("error", String(message), e.error?.stack);
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const r = e.reason as { message?: string; stack?: string } | string | undefined;
    const message =
      (typeof r === "object" && r?.message) ||
      (typeof r === "string" ? r : "Unhandled promise rejection");
    const stack = typeof r === "object" ? r?.stack : undefined;
    enqueue("error", String(message), stack);
  });

  // console.error abfangen — faengt u.a. React-Render-/Hydration-Fehler (#418).
  // Kein React-State, kein Re-Render -> verschlimmert keine Hydration.
  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    orig(...args);
    try {
      const { message, stack } = formatConsoleArgs(args);
      enqueue("error", message, stack);
    } catch {
      /* ignore */
    }
  };

  // Beim Verlassen/Verstecken der Seite restliche Queue per Beacon rausschieben.
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
